import {useEffect, useState} from 'react';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs.tsx';
import {Card, CardContent} from '@/components/ui/card.tsx';
import {CustomerFile} from '@/store/customerFile.ts';
import {CustomerFileSystems} from './CustomerFileSystems.tsx';
import {CustomerFileUsers} from './CustomerFileUsers.tsx';
import {CustomerFileInvoicesFull} from './CustomerFileInvoicesFull.tsx';
import {CustomerFileUnits} from './CustomerFileUnits.tsx';

export type CustomerFileTabValue = 'systems' | 'users' | 'invoices' | 'units';

interface Props {
    data: CustomerFile;
    canManage?: boolean;
    activeTab?: CustomerFileTabValue;
    onTabChange?: (value: CustomerFileTabValue) => void;
}

export function CustomerFileTabs({data, canManage, activeTab = 'systems', onTabChange}: Props) {
    const pendingCount = data.kpi.pending_invoices_count;
    // Track which tabs have ever been activated (lazy loading)
    const [loadedTabs, setLoadedTabs] = useState<Set<CustomerFileTabValue>>(() => new Set(['systems']));

    useEffect(() => {
        setLoadedTabs((prev) => {
            if (prev.has(activeTab)) return prev;
            return new Set([...prev, activeTab]);
        });
    }, [activeTab]);

    function handleTabChange(value: string) {
        const nextTab = value as CustomerFileTabValue;
        setLoadedTabs((prev) => {
            if (prev.has(nextTab)) return prev;
            return new Set([...prev, nextTab]);
        });
        onTabChange?.(nextTab);
    }

    return (
        <Card className="flex-1 min-w-0">
            <CardContent className="pt-4 px-2 sm:px-5">
                <Tabs value={activeTab} dir="rtl" onValueChange={handleTabChange}>
                    <TabsList className="mb-4 h-auto flex-wrap gap-y-1 py-1">
                        <TabsTrigger value="systems">מערכות</TabsTrigger>
                        <TabsTrigger value="users">משתמשים והרשאות</TabsTrigger>
                        <TabsTrigger value="invoices">
                            חשבוניות
                            {pendingCount > 0 && (
                                <span className="mr-1.5 inline-flex items-center justify-center w-4 h-4 text-[10px] rounded-full bg-amber-500 text-white">
                                    {pendingCount}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="units">סיכום שימוש ביחידות</TabsTrigger>
                    </TabsList>

                    <TabsContent value="systems">
                        <CustomerFileSystems systems={data.systems ?? []} customer={data.customer} />
                    </TabsContent>

                    <TabsContent value="users">
                        {loadedTabs.has('users') && (
                            <CustomerFileUsers users={data.users ?? []} systems={data.systems ?? []} currentUserRole={data.role} canManage={canManage} />
                        )}
                    </TabsContent>

                    <TabsContent value="invoices">
                        {loadedTabs.has('invoices') && (
                            <CustomerFileInvoicesFull invoices={data.invoices ?? []} />
                        )}
                    </TabsContent>

                    <TabsContent value="units">
                        {loadedTabs.has('units') && (
                            <CustomerFileUnits unitsMonths={data.units_months ?? []} />
                        )}
                    </TabsContent>
                </Tabs>
            </CardContent>
        </Card>
    );
}
